import { useEffect, useRef, useState } from 'react';
import type { AudioPreset, ShareMode } from './audio';
import type { MicLevel } from './micLevel';
import { BITRATE_OPTIONS, BROADCAST_BITRATE_OPTIONS } from './audio';

// Below this the panel is a docked full-width sheet, not a floating desk object:
// it can't be dragged, and it starts collapsed so it never lands on top of the
// meeting the moment you join. Kept in sync with the media query in index.css.
const NARROW = '(max-width: 640px)';

const PRESET_LABEL: Record<AudioPreset, string> = {
  voice: 'Voice',
  music: 'Music',
  broadcast: 'Broadcast',
};

interface SettingsPanelProps {
  preset: AudioPreset;
  switching: boolean;
  onPresetChange: (next: AudioPreset) => void;
  bitrateBps: number;
  onBitrateChange: (bps: number) => void;
  screensharing: boolean;
  shareMode: ShareMode;
  onShareModeChange: (next: ShareMode) => void;
  ncEnabled: boolean;
  onNcChange: (enabled: boolean) => void;
  ncMessage: string | null;
  mic: MicLevel;
  isAdmin: boolean;
  roomLocked: boolean;
  onUnlockAdmin: (password: string) => Promise<string | null>;
  onLockRoom: (passphrase: string) => Promise<string | null>;
  onUnlockRoom: () => Promise<string | null>;
}

export function SettingsPanel({
  preset,
  switching,
  onPresetChange,
  bitrateBps,
  onBitrateChange,
  screensharing,
  shareMode,
  onShareModeChange,
  ncEnabled,
  onNcChange,
  ncMessage,
  mic,
  isAdmin,
  roomLocked,
  onUnlockAdmin,
  onLockRoom,
  onUnlockRoom,
}: SettingsPanelProps) {
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [lockError, setLockError] = useState<string | null>(null);
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  const [minimised, setMinimised] = useState(narrow);
  // null = still parked at the CSS default (top/right); a drag pins it to left/top.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const grabRef = useRef<{ dx: number; dy: number } | null>(null);

  // Rotating a phone or resizing a window crosses the breakpoint, and a stale
  // `narrow` would keep applying the dragged inline position over the docked
  // layout (or leave the sheet draggable).
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    // Docked on narrow screens — nowhere to drag it to.
    if (narrow) return;
    // Let the minimise button take its own clicks.
    if ((e.target as HTMLElement).closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    grabRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setPos({ x: rect.left, y: rect.top }); // convert right-anchored → left-anchored before moving
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const grab = grabRef.current;
    const el = panelRef.current;
    if (!grab || !el) return;
    // Clamp so the panel can never be dragged out of reach.
    const maxX = window.innerWidth - el.offsetWidth;
    const maxY = window.innerHeight - el.offsetHeight;
    setPos({
      x: Math.min(Math.max(e.clientX - grab.dx, 0), Math.max(maxX, 0)),
      y: Math.min(Math.max(e.clientY - grab.dy, 0), Math.max(maxY, 0)),
    });
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    grabRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Clear stale lock feedback when the room's lock state flips.
  useEffect(() => {
    setLockError(null);
    setPassphrase('');
  }, [roomLocked]);

  const handleUnlockAdmin = async () => {
    const err = await onUnlockAdmin(adminPassword);
    if (err) {
      setAdminError(err);
    } else {
      setAdminError(null);
      setAdminPassword('');
    }
  };

  const handleLock = async () => {
    const err = await onLockRoom(passphrase);
    if (err) {
      setLockError(err);
    } else {
      setLockError(null);
      setPassphrase('');
    }
  };

  const handleUnlockRoom = async () => {
    const err = await onUnlockRoom();
    setLockError(err); // surface rate-limit / auth / network failures
  };
  // RNNoise stacks after capture-time DSP and would destroy music/shared audio,
  // so it's unavailable in the stereo presets (Music/Broadcast) — only Voice.
  const ncDisabled = preset !== 'voice';
  // Broadcast governs shared tab-audio fidelity, so it offers higher rates
  // (128/256/512); other presets keep the mic-oriented 16–128 range.
  const bitrateOptions = preset === 'broadcast' ? BROADCAST_BITRATE_OPTIONS : BITRATE_OPTIONS;
  return (
    <div
      className={`settings-panel${minimised ? ' minimised' : ''}`}
      ref={panelRef}
      style={pos && !narrow ? { left: pos.x, top: pos.y, right: 'auto' } : undefined}
    >
      <div
        className="settings-header"
        onPointerDown={startDrag}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/* Collapsed, the title carries the live state instead — the only way to
            read the current preset and bitrate without reopening the panel. */}
        <span className="settings-title">
          {minimised
            ? `${PRESET_LABEL[preset]} · ${Math.round(bitrateBps / 1000)} kbps`
            : 'Settings'}
        </span>
        <button
          type="button"
          className="settings-collapse"
          onClick={() => setMinimised((m) => !m)}
          aria-expanded={!minimised}
          aria-label={minimised ? 'Expand settings' : 'Minimise settings'}
          title={minimised ? 'Expand settings' : 'Minimise settings'}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 6.5 8 10.5 12 6.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {minimised ? null : (
        <>
        <div className="settings-group">
          <span className="settings-label">Audio</span>
          <div className="seg" role="group" aria-label="Audio preset">
            <button
              type="button"
              className={preset === 'voice' ? 'active' : ''}
              onClick={() => onPresetChange('voice')}
              disabled={switching || preset === 'voice'}
              title="Mono, echo/noise/AGC on — speech"
            >
              🎤 Voice
            </button>
            <button
              type="button"
              className={preset === 'music' ? 'active' : ''}
              onClick={() => onPresetChange('music')}
              disabled={switching || preset === 'music'}
              title="Stereo, no voice processing — music/high-fidelity"
            >
              🎵 Music
            </button>
            <button
              type="button"
              className={preset === 'broadcast' ? 'active' : ''}
              onClick={() => onPresetChange('broadcast')}
              disabled={switching || preset === 'broadcast'}
              title="Full-fidelity stereo for shared tab/system audio"
            >
              📡 Broadcast
            </button>
          </div>
          {preset === 'broadcast' ? (
            <span className="settings-note">
              macOS: share a <strong>Chrome Tab</strong> and tick “Share tab audio”.
            </span>
          ) : null}
          <div
            className={`mic-meter${mic.silent ? ' mic-meter-silent' : ''}`}
            role="meter"
            aria-label="Microphone input level"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(mic.level * 100)}
            title="Live microphone input level"
          >
            <span className="mic-meter-fill" style={{ width: `${Math.round(mic.level * 100)}%` }} />
          </div>
          {mic.silent ? (
            <span className="settings-note">
              Your mic appears silent — check the input device or OS mute.
            </span>
          ) : null}
        </div>

        <div className="settings-group">
          <span className="settings-label">
            Bitrate{switching ? '' : ` · ${Math.round(bitrateBps / 1000)} kbps`}
          </span>
          <div className="seg seg-bitrate" role="group" aria-label="Audio bitrate">
            {bitrateOptions.map((kbps) => {
              const bps = kbps * 1000;
              return (
                <button
                  type="button"
                  key={kbps}
                  className={bitrateBps === bps ? 'active' : ''}
                  onClick={() => onBitrateChange(bps)}
                  disabled={switching}
                  title={`${kbps} kbps Opus`}
                >
                  {kbps}
                </button>
              );
            })}
          </div>
        </div>

        <div className="settings-group">
          <span className="settings-label">Noise cancel</span>
          <div className="seg" role="group" aria-label="Noise cancellation">
            <button
              type="button"
              className={!ncEnabled ? 'active' : ''}
              onClick={() => onNcChange(false)}
              disabled={ncDisabled || switching || !ncEnabled}
              title={ncDisabled ? 'Unavailable in stereo presets — no voice processing' : 'No extra noise removal'}
            >
              Off
            </button>
            <button
              type="button"
              className={ncEnabled ? 'active' : ''}
              onClick={() => onNcChange(true)}
              disabled={ncDisabled || switching || ncEnabled}
              title={
                ncDisabled
                  ? 'Unavailable in stereo presets — no voice processing'
                  : 'RNNoise — strong noise removal applied after capture'
              }
            >
              On
            </button>
          </div>
          {ncMessage ? <span className="settings-note">{ncMessage}</span> : null}
        </div>

        {screensharing ? (
          <div className="settings-group">
            <span className="settings-label">Share mode</span>
            <div className="seg" role="group" aria-label="Screenshare mode">
              <button
                type="button"
                className={shareMode === 'detail' ? 'active' : ''}
                onClick={() => onShareModeChange('detail')}
                disabled={shareMode === 'detail'}
                title="Sharp text/code — high resolution, low frame rate"
              >
                📄 Detail
              </button>
              <button
                type="button"
                className={shareMode === 'motion' ? 'active' : ''}
                onClick={() => onShareModeChange('motion')}
                disabled={shareMode === 'motion'}
                title="Smooth video/gameplay — 720p, 30 fps"
              >
                🎬 Motion
              </button>
            </div>
          </div>
        ) : null}

        <div className="settings-group">
          <span className="settings-label">Admin</span>
          {!isAdmin ? (
            <>
              <input
                className="settings-input"
                type="password"
                placeholder="Admin password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                disabled={switching}
              />
              <button
                type="button"
                className="settings-btn"
                onClick={handleUnlockAdmin}
                disabled={switching || !adminPassword}
              >
                Unlock
              </button>
              {adminError ? <span className="settings-note">{adminError}</span> : null}
            </>
          ) : (
            <>
              <span className="settings-note settings-ok">Admin ✓</span>
              {!roomLocked ? (
                <>
                  <input
                    className="settings-input"
                    type="password"
                    placeholder="Room passphrase"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                  />
                  <button
                    type="button"
                    className="settings-btn"
                    onClick={handleLock}
                    disabled={!passphrase}
                  >
                    Lock room
                  </button>
                  {lockError ? <span className="settings-note">{lockError}</span> : null}
                </>
              ) : (
                <>
                  <span className="settings-note settings-ok">Locked 🔒</span>
                  <button type="button" className="settings-btn" onClick={handleUnlockRoom}>
                    Unlock
                  </button>
                  {lockError ? <span className="settings-note">{lockError}</span> : null}
                </>
              )}
            </>
          )}
        </div>
        </>
      )}
    </div>
  );
}
